import React from "react";
import Layout from "@/components/Layout";
import Breadcrumbs from "@/components/Breadcrumbs";
import usePageMeta from "@/hooks/use-page-meta";
import { Star, Quote } from "lucide-react";
import { TESTIMONIALS, GOOGLE_REVIEWS, BRAND } from "@/constants/content";

export default function TestimonialsPage() {
  usePageMeta({
    title: "Patient Testimonials — KinFlex Home Healthcare",
    description: "Real patient family testimonials from Delhi NCR. 4.9/5 average rating on Google. See what families say about KinFlex Home Healthcare.",
    canonical: `https://${BRAND.domain}/testimonials`,
  });

  const all = [
    ...TESTIMONIALS,
    ...GOOGLE_REVIEWS.highlights.map((h) => ({ name: h.name, text: h.text, rating: h.rating, role: "Google Review", location: "Delhi NCR" })),
  ];

  return (
    <Layout>
      <section className="pt-28 pb-6 md:pt-36 md:pb-12 bg-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-medical-grid opacity-60 pointer-events-none" />
        <div className="container-page relative max-w-4xl">
          <Breadcrumbs items={[{ label: "Testimonials" }]} />
          <div className="mt-5 eyebrow">Real Families, Real Stories</div>
          <h1 className="mt-2 font-display font-bold text-slate-900 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            What families say about <span className="bg-gradient-to-r from-[#0A84FF] to-[#10B981] bg-clip-text text-transparent">KinFlex</span>.
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            {GOOGLE_REVIEWS.averageRating}★ from {GOOGLE_REVIEWS.totalReviews}+ patients across Delhi NCR.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="container-page grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {all.map((t, i) => (
            <div key={i} className="rounded-2xl bg-white border border-slate-200/70 p-6 hover:shadow-lg transition-shadow relative" data-testid={`testimonial-page-${i}`}>
              <Quote className="h-6 w-6 absolute top-5 right-5" style={{ color: '#0A84FF', opacity: 0.25 }} />
              {t.image && (
                <div className="flex items-center gap-3 mb-4">
                  <img src={t.image} alt={t.name} className="h-11 w-11 rounded-full object-cover ring-2 ring-[#0A84FF]/15" />
                  <div>
                    <div className="font-display font-semibold text-slate-900 text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role} · {t.location}</div>
                  </div>
                </div>
              )}
              {!t.image && (
                <div className="mb-3">
                  <div className="font-display font-semibold text-slate-900 text-sm">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role} · {t.location}</div>
                </div>
              )}
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: t.rating || 5 }).map((_, s) => (
                  <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{t.text}</p>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}
